import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface UnitLessonCountDelimiterModalProps {
  open: boolean;
  delimiter: string;
  onDelimiterChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** true면 출판사 블록별 단원 카운트 안내를 추가한다(학습내용 관리 테이블). */
  publisherScoped?: boolean;
}

const UnitLessonCountDelimiterModal: React.FC<UnitLessonCountDelimiterModalProps> = ({
  open,
  delimiter,
  onDelimiterChange,
  onCancel,
  onConfirm,
  inputRef,
  publisherScoped = false,
}) => {
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      inputRef?.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, [inputRef, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onCancel();
      }}
    >
      <div
        className="cp-modal cp-program-page-split-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-lesson-count-delimiter-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-lesson-count-delimiter-title">대단원·소단원 구분 기호</h3>
        <p className="cp-program-page-split-desc">
          단원명에서 대단원과 소단원을 나누는 기호를 입력하세요. 기호 <strong>앞</strong> 부분(대단원)을 기준으로
          해당차시·전체차시를 카운트합니다.
          <br />
          비우면 단원명 전체를 기준으로 카운트합니다.
          <br />
          예: 단원 «식물의 구조-뿌리» · 기호 «-» → «식물의 구조» 기준
          {publisherScoped ? (
            <>
              <br />
              출판사가 같은 연속 구간마다 단원 기준 카운트가 새로 시작됩니다.
            </>
          ) : null}
        </p>
        <div className="cp-modal-body">
          <label htmlFor="cp-lesson-count-unit-delimiter" className="cp-program-page-split-field-label">
            구분 기호
          </label>
          <input
            ref={inputRef}
            id="cp-lesson-count-unit-delimiter"
            type="text"
            className="cp-program-page-split-delimiter-input"
            value={delimiter}
            onChange={(ev) => onDelimiterChange(ev.target.value)}
            onKeyDown={(ev) => {
              const native = ev.nativeEvent as KeyboardEvent;
              if (native.isComposing) return;
              if (ev.key === 'Escape') {
                ev.preventDefault();
                onCancel();
                return;
              }
              if (ev.key !== 'Enter') return;
              ev.preventDefault();
              onConfirm();
            }}
            placeholder="예: - 또는 · (비우면 단원 전체)"
            autoComplete="off"
          />
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onCancel}>
            취소
          </button>
          <button type="button" className="cp-btn-primary" onClick={onConfirm}>
            카운트하기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UnitLessonCountDelimiterModal;
